
import { Autocomplete, Avatar, AvatarGroup, Box, FormControl, TextField, useTheme } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { User } from 'types'
import { renderTeamOption } from '../Team/renderOptions'

interface UserOption extends User {
  user_uuid: string
  name: string
  image_512?: string
  role?: string
}

interface MultiUserSelectProps {
  name: string
  value: UserOption[]
  options: UserOption[]
  displayLabel: string
  onChange: (newUsers: UserOption[]) => void
  inputRef?: React.RefObject<HTMLInputElement>
  disabled?: boolean
  sx?: object
  singleSelect?: boolean // ✅ NEW
  isGridUpdate?: boolean
  wideMenu?: boolean
  max?: number
}

const MultiUserSelect: React.FC<MultiUserSelectProps> = ({
  name,
  value = [],
  options = [],
  displayLabel,
  onChange,
  inputRef,
  disabled,
  sx = {},
  singleSelect,
  isGridUpdate = false,
  max,
  wideMenu,
}) => {
  const theme = useTheme()
  const [selected, setSelected] = useState<UserOption[]>(value)

  useEffect(() => {
    setSelected(value || [])
  }, [value])

  // const handleChange = (_event: any, newValue: UserOption[]) => {
  //   setSelected(newValue)
  //   onChange(newValue)
  // }
  const handleChange = (_event: any, newValue: UserOption[] | null) => {
    if (singleSelect) {
      // Only use the *last* item the user picked (MUI will still give us all selected)
      if (Array.isArray(newValue)) {
        const newUser = newValue.find(user => !selected.some(s => s.user_uuid === user.user_uuid))
        const newArray = newUser ? [newUser] : selected.length ? [selected[0]] : []
        setSelected(newArray)
        onChange(newArray)
      }
    } else {
      setSelected(newValue || [])
      onChange(newValue || [])
    }
  }
  const handleGridLevelChange = (_event: any, newValue: any[]) => {
    if (Array.isArray(newValue)) {
      const newUser = newValue.find(user => !selected.some(s => s.user_uuid === user.user_uuid))
      const newArray = newUser ? [newUser] : selected.length ? [selected[0]] : []
      onChange(newArray as UserOption[])
    }
  }

  return (
    <Box sx={{ width: '100%', ...sx }}>
      <FormControl fullWidth>
        <Autocomplete
          multiple
          disableCloseOnSelect
          popupIcon={null}
          options={options}
          value={selected}
          getOptionLabel={option => {
            if (typeof option === 'string') return option
            if (!option || typeof option !== 'object') return ''
            return option.name || `${option.firstName ?? ''} ${option.lastName ?? ''}`.trim() || 'Unknown User'
          }}
          isOptionEqualToValue={(option, value) => {
            const a = option?.user_uuid || option?.id
            const b = value?.user_uuid || value?.id
            return a === b
          }}
          onChange={!isGridUpdate ? handleChange : handleGridLevelChange}
          filterOptions={(opts, { inputValue }) =>
            opts.filter(
              option =>
                option?.name?.toLowerCase()?.includes(inputValue?.toLowerCase()) ||
                option?.role?.toLowerCase()?.includes(inputValue?.toLowerCase())
            )
          }
          renderOption={(props, option, state) => renderTeamOption(props, option, state, theme)}
          renderTags={
            singleSelect
              ? tagValue =>
                  tagValue[0] ? (
                    <Avatar alt={tagValue[0].name} src={tagValue[0].image_512} sx={{ height: 36, width: 36 }} />
                  ) : null
              : (tagValue, getTagProps) => (
                  <AvatarGroup
                    max={max ?? 4}
                    spacing="small"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      '& .MuiAvatar-root': {
                        marginRight: 0.5,
                        height: 36,
                        width: 36,
                      },
                      '& .MuiInputBase-root': {
                        margin: 0,
                        p: 0,
                        display: 'flex',
                        flexWrap: 'nowrap', // prevent wrapping
                        alignItems: 'left',
                      },
                    }}
                  >
                    {tagValue.map((option, index) => {
                      const { key, onDelete, ...rest } = getTagProps({ index })
                      return (
                        <Avatar key={`${key}-${option.user_uuid}`} alt={option.name} src={option.image_512} {...rest} />
                      )
                    })}
                  </AvatarGroup>
                )
          }
          renderInput={params => (
            <TextField
              {...params}
              label={displayLabel}
              placeholder="Search users..."
              // autoFocus
              inputRef={inputRef}
              InputProps={{
                ...params.InputProps,
                endAdornment: null,
              }}
              sx={{
                maxHeight: '45px',
                '& .MuiAutocomplete-inputRoot': {
                  maxHeight: '45px',
                  height: '45px',
                  p: 0,
                  pl: 0.5,
                },
              }}
            />
          )}
          disabled={disabled}
          fullWidth
        />
      </FormControl>
    </Box>
  )
}

export default MultiUserSelect
